import { useRef, useState } from 'react'

import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { LoadingState } from '@/components/ui/LoadingState'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useCancelReservation } from '@/hooks/useCancelReservation'
import { useReservationRefundEstimate } from '@/hooks/useReservationRefundEstimate'
import { ApiError, toApiError } from '@/lib/api/errors'
import { newIdempotencyKey } from '@/lib/utils/idempotencyKey'
import { formatAmount, formatDateTime } from '@/lib/venue/format'
import type { ReservationCancelInput, ReservationCancelResultView } from '@/lib/venue/reservationCancelTypes'
import {
  hasCancelFormErrors,
  initialCancelFormState,
  MAX_CANCEL_REASON_DETAIL_LENGTH,
  REFUND_STATUS_TONE,
  RESERVATION_CANCEL_REASON_OPTIONS,
  sameCancelInput,
  toCancelInput,
  validateCancelForm,
  type CancelFormErrors,
} from '@/lib/venue/reservationCancelViewModel'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/** `refund.method === 'NONE'`(현장 수납·0원)이 아닐 때의 환불 상태 배지. */
function RefundStatusBadge({ refund }: { refund: ReservationCancelResultView['refund'] }) {
  if (refund.method === 'NONE') return <p className="text-sm text-slateish-600">환불할 금액이 없습니다.</p>

  const label =
    refund.status === 'COMPLETED' ? '환불 완료' : refund.status === 'FAILED' ? '환불 실패' : '환불 처리 중'
  return <Badge tone={REFUND_STATUS_TONE[refund.status]}>{label}</Badge>
}

/** 결과 화면 본문 — 요구사항 "환불 완료/처리 중(대기)/환불 실패+사유를 구분해 표시". */
function CancelResultView({ result, onConfirm }: { result: ReservationCancelResultView; onConfirm: () => void }) {
  const { refund } = result

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-semibold text-ink-700">예약이 취소됐습니다.</p>
        <p className="text-sm text-ink-700">
          {result.reservationNo ?? '-'} · {result.courtName} {formatDateTime(result.startsAt)} ~{' '}
          {formatDateTime(result.endsAt)}
        </p>
        <RefundStatusBadge refund={refund} />
      </div>

      {refund.method !== 'NONE' ? (
        <p className="text-sm text-ink-700">
          {refund.methodLabel} · 환불 {formatAmount(refund.refundAmount)}({refund.refundRatePercent}%)
        </p>
      ) : null}

      {refund.status === 'FAILED' ? (
        <Alert tone="danger">
          <p>환불 처리 중 문제가 발생했습니다. 운영팀에 문의해 주세요.</p>
          {refund.failureReason ? (
            <details className="mt-1 text-xs text-slateish-600">
              <summary className="cursor-pointer">상세 사유(참고용)</summary>
              {refund.failureReason}
            </details>
          ) : null}
        </Alert>
      ) : null}

      {refund.status === 'COMPLETED' && (refund.restoredPaidPoint || refund.restoredBonusPoint) ? (
        <p className="text-xs text-slateish-600">
          유료 포인트 {formatAmount(refund.restoredPaidPoint ?? 0)} · 보너스 포인트{' '}
          {formatAmount(refund.restoredBonusPoint ?? 0)} 복원됨
        </p>
      ) : null}

      <div className="flex justify-end pt-2">
        <Button type="button" onClick={onConfirm}>
          확인
        </Button>
      </div>
    </div>
  )
}

/**
 * 예약 취소 확인 다이얼로그(설계 §2-4 (2), #672) — 2단계 모달.
 *
 * 1단계(폼): 환불 예상액을 조회해 환불액·비율·차감액·결제수단·취소 가능 기한을 보여주고 사유를 입력받는다.
 * 2단계(결과): 취소 처리 성공 후 환불 완료/처리 중/실패를 구분해 보여준다 — 자동으로 닫지 않고 "확인"을
 * 눌러야 닫힌다(요구사항의 구분 표시를 사용자가 반드시 보게 하기 위해서, 설계 자율 결정 8).
 *
 * 🚨 성공 시 상세 쿼리를 캐시에 직접 합성하지 않고 invalidate한다(`useCancelReservation`) — 취소 응답이
 * 상세와 다른 타입이라 안전하게 병합할 수 없다(설계 자율 결정 3).
 */
export function ReservationCancelModal({
  venueId,
  reservationId,
  onClose,
}: {
  venueId: string
  reservationId: number
  onClose: () => void
}) {
  const estimateQuery = useReservationRefundEstimate(venueId, reservationId, true)
  const cancelMutation = useCancelReservation(venueId, reservationId)

  const [form, setForm] = useState(initialCancelFormState)
  const [errors, setErrors] = useState<CancelFormErrors>({})
  const [generalError, setGeneralError] = useState<ApiError | null>(null)
  const [result, setResult] = useState<ReservationCancelResultView | null>(null)

  // 🚨 재시도 시 바디가 이전 제출과 같으면 같은 키를 재사용(이중 환불 방지), 사유를 고쳐 쓰면 새 키를
  // 발급(서버가 "같은 키에 다른 바디"를 400으로 막는다) — 결제 플로우와 달리 sessionStorage 영속화는
  // 하지 않는다(리다이렉트 없는 동기 모달 액션이라 새로고침 생존이 필요 없다, 설계 자율 결정 4).
  const idempotencyKeyRef = useRef<{ key: string; input: ReservationCancelInput } | null>(null)

  function updateField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit() {
    const validationErrors = validateCancelForm(form)
    if (hasCancelFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    setGeneralError(null)

    const input = toCancelInput(form)
    const previous = idempotencyKeyRef.current
    const idempotencyKey = previous && sameCancelInput(previous.input, input) ? previous.key : newIdempotencyKey()
    idempotencyKeyRef.current = { key: idempotencyKey, input }

    try {
      const data = await cancelMutation.mutateAsync({ input, idempotencyKey })
      setResult(data)
    } catch (error) {
      setGeneralError(toApiError(error))
    }
  }

  return (
    <Modal title="예약 취소 처리" onClose={onClose}>
      {result ? (
        <CancelResultView result={result} onConfirm={onClose} />
      ) : (
        <div className="space-y-4">
          <Alert tone="danger">
            <p>취소 처리 시 환불 규정에 따라 자동 환불이 진행되며 되돌릴 수 없습니다.</p>
          </Alert>

          {estimateQuery.isLoading ? <LoadingState rows={3} label="환불 예상액을 조회하는 중" /> : null}

          {estimateQuery.isError ? (
            <Alert tone="danger">
              <p>{toApiError(estimateQuery.error).message}</p>
            </Alert>
          ) : null}

          {estimateQuery.data ? (
            <div className="space-y-2 rounded-lg bg-slateish-50 px-3 py-2 text-sm text-ink-700">
              <p>
                예약번호 {estimateQuery.data.reservationNo ?? '-'} · {estimateQuery.data.courtName}{' '}
                {formatDateTime(estimateQuery.data.startsAt)} ~ {formatDateTime(estimateQuery.data.endsAt)}
              </p>
              {estimateQuery.data.offlineRefund ? (
                <p>현장에서 {formatAmount(estimateQuery.data.policyRefundAmount)} 환불(서버 자동 환불 없음)</p>
              ) : (
                <p>
                  환불 예상액 {formatAmount(estimateQuery.data.refundAmount)}({estimateQuery.data.refundRatePercent}
                  %) · 차감 {formatAmount(estimateQuery.data.deductedAmount)} ·{' '}
                  {estimateQuery.data.paymentMethodLabel ?? '-'} → {estimateQuery.data.refundMethodLabel}
                </p>
              )}
              <p>{estimateQuery.data.cancellableUntilLabel}</p>
              {!estimateQuery.data.cancellable ? (
                <p className="text-danger-500">
                  취소 가능 기한이 지났습니다 — 그래도 제출하면 서버가 다시 판정합니다.
                </p>
              ) : null}
              {estimateQuery.data.additionalNotice ? (
                <p className="text-xs text-slateish-600">{estimateQuery.data.additionalNotice}</p>
              ) : null}
            </div>
          ) : null}

          {generalError ? (
            <Alert tone="danger">
              <p>{generalError.message}</p>
            </Alert>
          ) : null}

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="cancel-reason">
              취소 사유
            </label>
            <Select
              id="cancel-reason"
              value={form.reason}
              onChange={(event) => {
                updateField('reason', event.target.value as typeof form.reason)
              }}
            >
              {RESERVATION_CANCEL_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="cancel-reason-detail">
              상세 사유(선택)
            </label>
            <Textarea
              id="cancel-reason-detail"
              rows={2}
              maxLength={MAX_CANCEL_REASON_DETAIL_LENGTH}
              value={form.reasonDetail}
              placeholder="예) 코트 침수로 운영 불가"
              aria-invalid={Boolean(errors.reasonDetail)}
              onChange={(event) => {
                updateField('reasonDetail', event.target.value)
              }}
            />
            <FieldError message={errors.reasonDetail} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              돌아가기
            </Button>
            <Button
              type="button"
              disabled={cancelMutation.isPending || !estimateQuery.data}
              onClick={() => {
                void handleSubmit()
              }}
            >
              {cancelMutation.isPending ? '처리 중…' : '취소 처리'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
